"use client";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  FileSpreadsheet,
  FileText,
  Users,
  DollarSign,
  Calendar,
  UserCheck,
} from "lucide-react";
import { exportToExcel, exportToPDF } from "@/lib/excelExport";
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { formatCurrency } from "@/lib/utils";

interface OnlineBookingDashboardProps {
  data: {
    dateRange: {
      start: string;
      end: string;
    };
    summary: {
      totalBookings: number;
      byCategory: {
        occasional: number;
        regular: number;
        loyal: number;
      };
      totalRevenue: number;
    };
    dailyData: Array<{
      date: string;
      byCategory: {
        occasional: { count: number; revenue: number };
        regular: { count: number; revenue: number };
        loyal: { count: number; revenue: number };
      };
      total: { count: number; revenue: number };
    }>;
    records: Array<{
      appointmentId: string;
      date: string;
      customerName: string;
      customerCategory: string;
      vehiclePlate: string;
      serviceType: string;
      assignedTo: string;
      status: string;
      amount: number;
    }>;
  };
}

const COLORS = {
  occasional: "#f59e0b",
  regular: "#3b82f6",
  loyal: "#10b981",
};

export function OnlineBookingDashboard({ data }: OnlineBookingDashboardProps) {
  // Defensive defaults for potentially undefined data
  const dailyData = data?.dailyData || [];
  const records = data?.records || [];
  const summary = data?.summary || {
    totalBookings: 0,
    byCategory: { occasional: 0, regular: 0, loyal: 0 },
    totalRevenue: 0,
  };

  const getExportData = () => {
    return records.map((record) => ({
      "Appointment ID": record.appointmentId,
      Date: format(new Date(record.date), "dd/MM/yyyy"),
      Customer: record.customerName,
      Category: record.customerCategory,
      "Vehicle Plate": record.vehiclePlate,
      "Service Type": record.serviceType,
      "Assigned To": record.assignedTo,
      Status: record.status,
      Amount: record.amount,
    }));
  };

  const getDailySummaryData = () => {
    const exportData = dailyData.map((row) => ({
      Date: format(new Date(row.date), "dd/MM/yyyy"),
      "Occasional Count": row.byCategory.occasional.count,
      "Occasional Revenue": row.byCategory.occasional.revenue,
      "Regular Count": row.byCategory.regular.count,
      "Regular Revenue": row.byCategory.regular.revenue,
      "Loyal Count": row.byCategory.loyal.count,
      "Loyal Revenue": row.byCategory.loyal.revenue,
      "Total Count": row.total.count,
      "Total Revenue": row.total.revenue,
    }));

    // Add totals row
    exportData.push({
      Date: "TOTAL",
      "Occasional Count": summary.byCategory.occasional,
      "Occasional Revenue": dailyData.reduce(
        (sum, d) => sum + d.byCategory.occasional.revenue,
        0,
      ),
      "Regular Count": summary.byCategory.regular,
      "Regular Revenue": dailyData.reduce(
        (sum, d) => sum + d.byCategory.regular.revenue,
        0,
      ),
      "Loyal Count": summary.byCategory.loyal,
      "Loyal Revenue": dailyData.reduce(
        (sum, d) => sum + d.byCategory.loyal.revenue,
        0,
      ),
      "Total Count": summary.totalBookings,
      "Total Revenue": summary.totalRevenue,
    });

    return exportData;
  };

  const handleExportExcel = () => {
    exportToExcel(getExportData(), "Online_Booking_Report", "Online Bookings");
  };

  const handleExportPDF = () => {
    exportToPDF(getExportData(), "Online_Booking_Report", {
      title: "Online Booking Report",
      subtitle: "Online bookings breakdown by customer category",
      dateRange: data.dateRange,
    });
  };

  const handleExportSummaryExcel = () => {
    exportToExcel(
      getDailySummaryData(),
      "Online_Booking_Summary",
      "Daily Summary",
    );
  };

  // Transform data for chart
  const chartData = dailyData.map((row) => ({
    date: row.date,
    Occasional: row.byCategory.occasional.count,
    Regular: row.byCategory.regular.count,
    Loyal: row.byCategory.loyal.count,
  }));

  const getCategoryBadge = (category: string) => {
    const categoryLower = category.toLowerCase();
    switch (categoryLower) {
      case "loyal":
        return <Badge className="bg-green-100 text-green-800">Loyal</Badge>;
      case "regular":
        return <Badge className="bg-blue-100 text-blue-800">Regular</Badge>;
      case "occasional":
      default:
        return (
          <Badge className="bg-amber-100 text-amber-800">Occasional</Badge>
        );
    }
  };

  const getStatusBadge = (status: string) => {
    const statusLower = status.toLowerCase();
    if (statusLower === "completed") {
      return <Badge className="bg-green-100 text-green-800">Completed</Badge>;
    } else if (statusLower === "cancelled") {
      return <Badge className="bg-red-100 text-red-800">Cancelled</Badge>;
    } else if (statusLower === "in_progress" || statusLower === "inprogress") {
      return <Badge className="bg-blue-100 text-blue-800">In Progress</Badge>;
    }
    return <Badge variant="outline">{status}</Badge>;
  };

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-5">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              Total Bookings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary.totalBookings}</div>
            <p className="text-xs text-muted-foreground">Online bookings</p>
          </CardContent>
        </Card>

        <Card className="border-amber-200 dark:border-amber-900">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Users className="h-4 w-4 text-amber-500" />
              Occasional
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-600">
              {summary.byCategory.occasional}
            </div>
            <p className="text-xs text-muted-foreground">One-time customers</p>
          </CardContent>
        </Card>

        <Card className="border-blue-200 dark:border-blue-900">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <UserCheck className="h-4 w-4 text-blue-500" />
              Regular
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {summary.byCategory.regular}
            </div>
            <p className="text-xs text-muted-foreground">Repeat customers</p>
          </CardContent>
        </Card>

        <Card className="border-green-200 dark:border-green-900">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <UserCheck className="h-4 w-4 text-green-500" />
              Loyal
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {summary.byCategory.loyal}
            </div>
            <p className="text-xs text-muted-foreground">Loyal customers</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-muted-foreground" />
              Total Revenue
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {formatCurrency(summary.totalRevenue)}
            </div>
            <p className="text-xs text-muted-foreground">
              From online bookings
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Chart */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Online Bookings by Category</CardTitle>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportSummaryExcel}
            >
              <FileSpreadsheet className="h-4 w-4 mr-2" />
              Summary Excel
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportExcel}>
              <FileSpreadsheet className="h-4 w-4 mr-2" />
              Excel
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportPDF}>
              <FileText className="h-4 w-4 mr-2" />
              PDF
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tickFormatter={(value) => format(new Date(value), "dd MMM")}
              />
              <YAxis />
              <Tooltip
                labelFormatter={(value) =>
                  format(new Date(value), "dd MMM yyyy")
                }
              />
              <Legend />
              <Bar dataKey="Occasional" fill={COLORS.occasional} />
              <Bar dataKey="Regular" fill={COLORS.regular} />
              <Bar dataKey="Loyal" fill={COLORS.loyal} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Daily Summary Table */}
      <Card>
        <CardHeader>
          <CardTitle>Daily Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead className="text-right">Occasional</TableHead>
                <TableHead className="text-right">Regular</TableHead>
                <TableHead className="text-right">Loyal</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">Revenue</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {dailyData.map((row) => (
                <TableRow key={row.date}>
                  <TableCell>
                    {format(new Date(row.date), "dd MMM yyyy")}
                  </TableCell>
                  <TableCell className="text-right">
                    {row.byCategory.occasional.count}
                  </TableCell>
                  <TableCell className="text-right">
                    {row.byCategory.regular.count}
                  </TableCell>
                  <TableCell className="text-right">
                    {row.byCategory.loyal.count}
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {row.total.count}
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {formatCurrency(row.total.revenue)}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow className="bg-muted/50 font-bold">
                <TableCell>TOTAL</TableCell>
                <TableCell className="text-right">
                  {summary.byCategory.occasional}
                </TableCell>
                <TableCell className="text-right">
                  {summary.byCategory.regular}
                </TableCell>
                <TableCell className="text-right">
                  {summary.byCategory.loyal}
                </TableCell>
                <TableCell className="text-right">
                  {summary.totalBookings}
                </TableCell>
                <TableCell className="text-right">
                  {formatCurrency(summary.totalRevenue)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Detailed Records Table */}
      <Card>
        <CardHeader>
          <CardTitle>Booking Records</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Vehicle</TableHead>
                <TableHead>Service</TableHead>
                <TableHead>Assigned To</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {records.map((record) => (
                <TableRow key={record.appointmentId}>
                  <TableCell>
                    {format(new Date(record.date), "dd MMM yyyy")}
                  </TableCell>
                  <TableCell>{record.customerName}</TableCell>
                  <TableCell>
                    {getCategoryBadge(record.customerCategory)}
                  </TableCell>
                  <TableCell>{record.vehiclePlate}</TableCell>
                  <TableCell>{record.serviceType}</TableCell>
                  <TableCell>{record.assignedTo}</TableCell>
                  <TableCell>{getStatusBadge(record.status)}</TableCell>
                  <TableCell className="text-right">
                    {formatCurrency(record.amount)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
